import React, { useEffect, useState } from "react";
import Tabs from "@amzn/awsui-components-react-v3/polaris/tabs";
import { Dispatch } from "redux";
import { Facebook } from "react-content-loader";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import { MetricsState, selectors } from "../../logging";
import { useHttpRequest, LoadingState } from "../../http";
import styles from "../layout_styles.css";
import {
  TeamOwnerDetailsDto,
  TeamOwnerDetailsResponseDto,
} from "../../common/model/teamownerdetails.dto";
import DefaultTeamLogoSvg from "../../svg/DefaultTeamLogo";
import { RootState } from "../../redux/rootReducer";
import TeamDetailsPage from "../../details/TeamDetailsPage";
import { TeamDtoWithBreadcrumb } from "./SubHeaderDto";
import PermissionsPage from "../../permissions/PermissionsPage";
import helpPanelSlice, {
  DisplayHelpPanel,
} from "../../help_panel/helpPanelSlice";
import subheaderSlice, {
  GetSubheaderDetailFetchPayload,
} from "./subheaderSlice";
import { DisplayBanner } from "../../banner/bannerSlice";
import SubheaderData from "./SubheaderData";
import TeamsReportsToBreadcrumbs from "../../common/breadcrumbs/TeamsReportsToBreadcrumbs";
import Banner from "../../banner/Banner";
import "../../common/breadcrumbs/breadcrumbs.module.css";
import OrphanTeam from "../../common/utils/OrphanTeam";
import { Box, SpaceBetween } from "@amzn/awsui-components-react-v3";

interface SubheaderStateProps {
  subheaderDetail: TeamDtoWithBreadcrumb;
  loadingState: LoadingState;
  displayBanner: DisplayBanner | undefined;
  metrics: MetricsState;
}

interface SubHeaderDispatchProps {
  getSubheaderDetailFetch: (payload: GetSubheaderDetailFetchPayload) => void;
  setHelpPanelContent: (payload: DisplayHelpPanel | undefined) => void;
  setHelpPanelOpenState: (payload: boolean | true) => void;
}

export interface SubheaderProps {
  subheaderData: SubheaderData;
}

// displays the help panel in collapsed state for the active tab
const showHelpPanel = (
  tabId: string,
  setHelpPanelContent: (payload: DisplayHelpPanel | undefined) => void,
  setHelpPanelOpenState: (payload: boolean | true) => void
): void => {
  if (tabId === "permissions") {
    setHelpPanelContent({
      header: "User permissions",
      contentType: "permissions",
      widenContent: true,
    });
  } else {
    setHelpPanelContent({
      header: "Team hierarchy",
      contentType: "teamDetails",
      widenContent: true,
    });
  }
  setHelpPanelOpenState(false);
};

const DETAILS_TAB = "details";
const PERMISSIONS_TAB = "permissions";

export const Subheader: React.FC<SubheaderProps &
  SubheaderStateProps &
  SubHeaderDispatchProps> = ({
  subheaderDetail,
  subheaderData,
  getSubheaderDetailFetch,
  loadingState,
  metrics,
  displayBanner,
  setHelpPanelContent,
  setHelpPanelOpenState,
}): JSX.Element => {
  const history = useHistory();

  const getTab = (): string => {
    return [DETAILS_TAB, PERMISSIONS_TAB].includes(
      history.location.hash.substring(1)
    )
      ? history.location.hash.substring(1)
      : DETAILS_TAB;
  };

  const [activeTab, setActiveTab] = useState<string>(getTab());

  useEffect(() => {
    showHelpPanel(activeTab, setHelpPanelContent, setHelpPanelOpenState);
  }, [activeTab, setHelpPanelContent, setHelpPanelOpenState]);

  useEffect(() => {
    getSubheaderDetailFetch({
      id: subheaderData.id as string,
    });
  }, [subheaderData.id, getSubheaderDetailFetch]);

  const {
    sendRequest: sendEmployeeDetailsRequest,
    data: teamOwnerDetails,
    loadingState: teamOwnerLoading,
  } = useHttpRequest<TeamOwnerDetailsResponseDto>("ownerDetails", metrics);

  useEffect(() => {
    if (subheaderDetail) {
      const teamAdminIds =
        subheaderDetail.owner && subheaderDetail.owner !== null
          ? subheaderDetail.owner.employeeId
          : undefined;
      sendEmployeeDetailsRequest({
        url: "/api/v1/teams/query/employees",
        method: "POST",
        data: {
          employeeIds: teamAdminIds ? [teamAdminIds] : [],
        },
      });
    }
  }, [subheaderDetail, sendEmployeeDetailsRequest]);

  useEffect(() => {
    /**
     * If we're unable to resolve the team owner, we can assume that the current team's owner
     * is bad/stale data - and the UI should treat it as if the team does not have an owner.
     */
    if (
      teamOwnerLoading === LoadingState.RESOLVED &&
      teamOwnerDetails &&
      teamOwnerDetails.data.length === 0
    ) {
      subheaderDetail.owner = undefined;
    }
  }, [teamOwnerDetails, teamOwnerLoading]);

  const getSubheader = (
    admin: TeamOwnerDetailsDto | undefined
  ): JSX.Element => {
    const teamname = subheaderDetail.name;
    const ownerDisplay: JSX.Element = admin ? (
      <div>{`Owned by ${admin.employeeFullName}`}</div>
    ) : (
      <div>No owner assigned</div>
    );
    return (
      <SpaceBetween size="s" direction="horizontal">
        <div className={styles["logo-cropper"]}>
          <DefaultTeamLogoSvg />
        </div>
        <SpaceBetween size="xxs">
          <h1 className={styles["overflow-text"]}>{teamname}</h1>
          {ownerDisplay}
        </SpaceBetween>
      </SpaceBetween>
    );
  };

  const details = teamOwnerDetails ? (
    <TeamDetailsPage
      team={subheaderDetail}
      ownerDetails={
        teamOwnerDetails.data.length > 0 ? teamOwnerDetails.data[0] : undefined
      }
      metrics={metrics}
    />
  ) : (
    <div />
  );
  const permissions = subheaderDetail ? (
    <PermissionsPage team={subheaderDetail} metrics={metrics} />
  ) : (
    <div />
  );

  const tabs = [
    { label: "Details", id: DETAILS_TAB, href: DETAILS_TAB, content: details },
    {
      label: "Permissions",
      id: PERMISSIONS_TAB,
      href: PERMISSIONS_TAB,
      content: permissions,
    },
  ];

  const currentTab = (
    <Tabs
      tabs={tabs}
      activeTabId={activeTab}
      onChange={(event) => {
        setActiveTab(event.detail.activeTabId);
        showHelpPanel(
          event.detail.activeTabId,
          setHelpPanelContent,
          setHelpPanelOpenState
        );
        history.push(
          `${history.location.pathname}#${event.detail.activeTabId}`
        );
      }}
    />
  );

  let breadcrumbs;
  let subheader;

  if (
    loadingState === LoadingState.RESOLVED &&
    teamOwnerLoading === LoadingState.RESOLVED
  ) {
    const shouldShowOrphanTeamBreadcrumbs =
      subheaderDetail.breadcrumbs !== undefined &&
      subheaderDetail.breadcrumbs.length === 1 &&
      subheaderDetail.breadcrumbs[0].name !== "Amazon";

    breadcrumbs = (
      <div className={styles["breadcrumb-outer"]}>
        <div className={styles["breadcrumb-wrapper"]}>
          {shouldShowOrphanTeamBreadcrumbs ? (
            <OrphanTeam />
          ) : (
            <TeamsReportsToBreadcrumbs data={subheaderDetail} />
          )}
        </div>
      </div>
    );

    subheader = teamOwnerDetails && (
      <SpaceBetween size="l">
        {getSubheader(
          teamOwnerDetails.data.length > 0
            ? teamOwnerDetails.data[0]
            : undefined
        )}
        {currentTab}
      </SpaceBetween>
    );
  } else {
    breadcrumbs = <div />;
    subheader = <Facebook />;
  }

  return (
    <>
      {breadcrumbs}
      {displayBanner && <Banner />}
      <Box padding="xxl">{subheader}</Box>
    </>
  );
};

const mapStateToProperties = (state: RootState): SubheaderStateProps => {
  return {
    // Any is hacking the possible undefined state of that props
    subheaderDetail: state.subheader.subheaderDetail as any,
    loadingState: state.subheader.loadingState,
    metrics: selectors.getMetrics(state),
    displayBanner: state.banner.displayBanner,
  };
};

const mapDispatchToProperties = (
  dispatch: Dispatch
): SubHeaderDispatchProps => ({
  getSubheaderDetailFetch: (payload: GetSubheaderDetailFetchPayload) =>
    dispatch(subheaderSlice.actions.getSubheaderDetailFetch(payload)),
  setHelpPanelContent(helpPanelContent: DisplayHelpPanel | undefined) {
    dispatch(helpPanelSlice.actions.setHelpPanelContent(helpPanelContent));
  },
  setHelpPanelOpenState(helpPanelState: boolean) {
    dispatch(helpPanelSlice.actions.setHelpPanelOpenState(helpPanelState));
  },
});

export default connect(
  mapStateToProperties,
  mapDispatchToProperties
)(Subheader);
